import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getSshKeyListV4QueryKey,
  useHostingVpsSshKeySync,
  useSshKeyListV4,
} from '@newfold/huapi-js';
import { SshKeyListV4200RowsItem } from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';
import { SSHKey } from '~/components/SSHKeysTable';
import useAccount from '~/hooks/useAccount';
import { getListBySearch } from '~/utils/common/helpers';
import { formatAddedAt } from '~/utils/date/helpers';

import { getAlgorithmType } from '../utils/helper';

const DEFAULT_ROWS_PER_PAGE = 10;

type SshKeyRow = SshKeyListV4200RowsItem;

const normalizeKey = (
  key: SshKeyRow,
  index: number,
  hasRequestedServerScan: boolean,
  reAddedServerKeyIds: Set<number>,
): SSHKey | null => {
  const { name, created_at: createdAt, pubkey = '', authorize, remote } = key;

  if (typeof key.id !== 'number' || !Number.isFinite(key.id)) {
    return null;
  }

  const id = key.id;

  const isReAdded = reAddedServerKeyIds.has(id);

  const isMissingFromServer =
    hasRequestedServerScan && remote !== true && !isReAdded;

  const addedAt = formatAddedAt(createdAt);

  return {
    id,
    name: name || `key-${index + 1}`,
    alg_type: getAlgorithmType(key),
    addedAt,
    createdAt,
    keyValue: pubkey,
    authorize,
    status: isMissingFromServer ? 'notOnServer' : 'active',
    deletedFromServerAt: isMissingFromServer ? addedAt : undefined,
  };
};

export const useSSHKeyList = () => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'sshSettings',
  });
  const generateAlert = useAlert();
  const { id: hostingId } = useAccount();
  const queryClient = useQueryClient();

  const [hasScannedServer, setHasScannedServer] = useState(false);
  const [reAddedServerKeyIds, setReAddedServerKeyIds] = useState<number[]>([]);
  const [reAddingKeyId, setReAddingKeyId] = useState<number | null>(null);
  const [isScanningServer, setIsScanningServer] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const sshKeyListParams = useMemo(
    () => ({
      scan: hasScannedServer,
    }),
    [hasScannedServer],
  );

  const {
    data: sshKeyRows,
    isPending: isKeysPending,
    isError: isKeysError,
    isFetching: isKeysFetching,
    refetch: refetchSshKeyList,
  } = useSshKeyListV4(hostingId, sshKeyListParams, {
    query: {
      enabled: Boolean(hostingId),
      select: ({ data }) => data?.rows ?? [],
    },
  });

  const { mutateAsync: syncKeyWithServer, isPending: isSyncPending } =
    useHostingVpsSshKeySync();

  const reAddedServerKeyIdSet = useMemo(
    () => new Set(reAddedServerKeyIds),
    [reAddedServerKeyIds],
  );

  const keys = useMemo<SSHKey[]>(
    () =>
      (sshKeyRows ?? [])
        .map((key, index) =>
          normalizeKey(key, index, hasScannedServer, reAddedServerKeyIdSet),
        )
        .filter((key): key is SSHKey => key !== null),
    [hasScannedServer, sshKeyRows, reAddedServerKeyIdSet],
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredKeys = useMemo(() => {
    if (!normalizedSearchQuery) {
      return keys;
    }

    return getListBySearch(keys, normalizedSearchQuery, ['name']) as SSHKey[];
  }, [keys, normalizedSearchQuery]);

  const paginatedKeys = useMemo(() => {
    const startIndex = page * rowsPerPage;

    return filteredKeys.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredKeys, page, rowsPerPage]);

  const keysById = useMemo(
    () => new Map(keys.map((row) => [row.id, row])),
    [keys],
  );

  const missingServerKeysCount = keys.filter(
    ({ status }) => status === 'notOnServer',
  ).length;

  const hasItems = keys.length > 0;
  const hasSearchResults = filteredKeys.length > 0;
  const hasMissingKeys = missingServerKeysCount > 0;

  const isTableLoading = isKeysPending || (isScanningServer && isKeysFetching);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    nextPage: number,
  ) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const getKeyById = (id: number) => keysById.get(id);

  const invalidateKeyList = async () => {
    await queryClient.invalidateQueries({
      queryKey: getSshKeyListV4QueryKey(hostingId, sshKeyListParams),
    });
  };

  const handleSyncKeyById = async (id: number) => {
    const key = getKeyById(id);

    if (!key || !hostingId) {
      return;
    }

    try {
      setReAddingKeyId(id);

      const syncResponse = await syncKeyWithServer({
        hostingId,
        id,
      });

      const syncedKeyName = syncResponse?.data?.name || key.name;

      setReAddedServerKeyIds((existingKeyIds) =>
        existingKeyIds.includes(id) ? existingKeyIds : [...existingKeyIds, id],
      );

      await invalidateKeyList();
      setLastSyncedAt(new Date());
      generateAlert({
        severity: 'success',
        description: t('reAddSuccessToast', {
          keyName: syncedKeyName,
        }),
        showCloseBtn: true,
      });
    } catch {
      generateAlert({
        severity: 'error',
        description: t('reAddErrorToast'),
        showCloseBtn: true,
      });
    } finally {
      setReAddingKeyId(null);
    }
  };

  const handleSyncWithServer = async () => {
    if (!hostingId) {
      return;
    }

    setReAddedServerKeyIds([]);

    if (!hasScannedServer) {
      setHasScannedServer(true);
    }

    try {
      setIsScanningServer(true);

      const syncResponse = await refetchSshKeyList();

      if (syncResponse?.error) {
        throw syncResponse.error;
      }

      await invalidateKeyList();
      setLastSyncedAt(new Date());
    } catch {
      generateAlert({
        severity: 'error',
        description: t('reAddErrorToast'),
        showCloseBtn: true,
      });
    } finally {
      setIsScanningServer(false);
    }
  };

  return {
    keys,
    filteredKeys,
    paginatedKeys,
    isTableLoading,
    isKeysPending,
    isKeysError,
    isSyncPending,
    reAddingKeyId,
    lastSyncedAt,
    hasScannedServer,
    searchQuery,
    page,
    rowsPerPage,
    hasItems,
    hasSearchResults,
    hasMissingKeys,
    missingServerKeysCount,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    getKeyById,
    syncKeyById: handleSyncKeyById,
    syncWithServer: handleSyncWithServer,
  };
};

export default useSSHKeyList;
